// ── Transaction Engine ────────────────────────────────────────────────────────
// Pure functions — data in, data out. No React, no side effects.

/** Filter transaksi untuk bulan & tahun tertentu */
export function filterByMonth(transactions, month, year) {
  return transactions.filter(t => {
    const d = new Date(t.date);
    return d.getMonth() === month && d.getFullYear() === year;
  });
}

/** Filter transaksi untuk satu tahun */
export function filterByYear(transactions, year) {
  return transactions.filter(t => new Date(t.date).getFullYear() === year);
}

/** Hitung total jumlah berdasarkan tipe transaksi */
export function sumByType(transactions, type) {
  return transactions.filter(t => t.type === type).reduce((a, t) => a + t.amount, 0);
}

/**
 * Summary keuangan untuk satu bulan
 * @returns {{ income, expense, investasi, net, transfers }}
 */
export function getMonthlySummary(transactions, month, year) {
  const txs      = filterByMonth(transactions, month, year);
  const income   = sumByType(txs, "income");
  const expense  = sumByType(txs, "expense");
  const investasi= sumByType(txs, "investasi");
  const transfers= sumByType(txs, "transfer");
  return { income, expense, investasi, net: income - expense - investasi, transfers };
}

/**
 * Summary keuangan untuk satu tahun
 * @returns {{ income, expense, investasi, net }}
 */
export function getYearlySummary(transactions, year) {
  const txs      = filterByYear(transactions, year);
  const income   = sumByType(txs, "income");
  const expense  = sumByType(txs, "expense");
  const investasi= sumByType(txs, "investasi");
  return { income, expense, investasi, net: income - expense - investasi };
}

/**
 * Breakdown per bulan untuk chart tahunan (12 bulan)
 * @returns {Array<{ label, month, income, expense, investasi, net }>}
 */
export function getMonthlyBreakdown(transactions, year, monthLabels) {
  const yearTxs = filterByYear(transactions, year);
  return monthLabels.map((label, m) => {
    const txs      = yearTxs.filter(t => new Date(t.date).getMonth() === m);
    const income   = sumByType(txs, "income");
    const expense  = sumByType(txs, "expense");
    const investasi= sumByType(txs, "investasi");
    return { label, month: m, income, expense, investasi, net: income - expense - investasi };
  });
}

/**
 * Pengeluaran per kategori untuk periode tertentu
 * @returns {Array<{ categoryId, name, icon, color, total, pct }>}
 */
export function getExpenseByCategory(transactions, categories, month, year, mode = "month") {
  const txs = mode === "year"
    ? filterByYear(transactions, year).filter(t => t.type === "expense")
    : filterByMonth(transactions, month, year).filter(t => t.type === "expense");

  const totalExp = txs.reduce((a, t) => a + t.amount, 0);
  const map = {};
  txs.forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.amount; });

  return Object.entries(map).map(([id, total]) => {
    const cat = categories.find(c => c.id === id);
    return {
      categoryId: id,
      name:  cat?.name  || "Lainnya",
      icon:  cat?.icon  || "📤",
      color: cat?.color || "#666",
      total,
      pct: totalExp > 0 ? (total / totalExp) * 100 : 0,
    };
  }).sort((a, b) => b.total - a.total);
}

/**
 * Savings rate bulan tertentu = (investasi + saldo bersih) / income
 */
export function getSavingsRate(transactions, month, year) {
  const { income, net, investasi } = getMonthlySummary(transactions, month, year);
  return income > 0 ? (net + investasi) / income : 0;
}

/**
 * MoM (month-over-month) comparison untuk satu bulan
 * @returns {{ income, expense, investasi, net, prevIncome, prevExpense, prevInvestasi, prevNet }}
 */
export function getMoMComparison(transactions, month, year) {
  const curr = getMonthlySummary(transactions, month, year);
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear  = month === 0 ? year - 1 : year;
  const prev = getMonthlySummary(transactions, prevMonth, prevYear);
  return {
    ...curr,
    prevIncome:    prev.income,
    prevExpense:   prev.expense,
    prevInvestasi: prev.investasi,
    prevNet:       prev.net,
  };
}
